import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import Datastore from "../../communication/Datastore";
import { REHYDRATE } from "redux-persist";
import store from "../../state/Store";
import MessageLog from "../../types/system/MessageLog";
import PageSpecification from "../../types/system/PageSpecification";
import MessageLogRecipient from "../../types/system/MessageLogRecipient";

const imageRegex = /"(cid:(.+?))"/g;

// Define the type for our state store entry
type MessageLogState = {
    allData: Record<number, MessageLog[]>;
    pageData: MessageLog[];
    virtualSpec: PageSpecification;
    internalSpec: PageSpecification;
    status: string;
    openMessage?: MessageLog;
    openRecipients?: MessageLogRecipient[];
    openStatus: string;
    error: string;
};

// Define the initial state for our state store entry
const initialState: MessageLogState = {
    allData: {},
    pageData: [],
    virtualSpec: {
        page: 0,
        size: 20,
        total: 0,
    } as PageSpecification,
    internalSpec: {
        page: -1,
        size: 200,
        total: 0,
    } as PageSpecification,
    status: "idle",
    openStatus: "idle",
    error: null,
};

// Create a slice to hold all our reducers
const messageLogSlice = createSlice({
    name: "messageLog",
    initialState: initialState,
    reducers: {
        setVirtualPageAndSize: (
            state,
            action: PayloadAction<PageSpecification>,
        ) => {
            state.virtualSpec.page = action.payload.page;
            state.virtualSpec.size = action.payload.size;

            const internalPageIndex = Math.floor(
                (state.virtualSpec.page * state.virtualSpec.size) /
                    state.internalSpec.size,
            );
            const internalPage = state.allData[internalPageIndex];
            const translatedPageStart =
                state.virtualSpec.page * state.virtualSpec.size -
                internalPageIndex * state.internalSpec.size;
            const translatedPageEnd =
                translatedPageStart + state.virtualSpec.size;

            state.pageData =
                internalPage?.slice(
                    Math.min(translatedPageStart, internalPage.length),
                    Math.min(translatedPageEnd, internalPage.length),
                ) ?? [];

            state.virtualSpec.waiting =
                state.virtualSpec.loading && state.pageData.length === 0;
        },
        clearBlock: (state, action: PayloadAction<MessageLog>) => {
            let found = false;
            Object.entries(state.allData).forEach(block => {
                if (
                    found ||
                    block[1].includes(action.payload) ||
                    block[1].find(item => item.id === action.payload.id)
                ) {
                    delete state.allData[block[0]];
                    found = true;
                }
            });
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;

                const allData = {};

                if (rawData?.payload?.messageLog?.allData)
                    for (let entry in rawData?.payload?.messageLog.allData)
                        allData[entry] = rawData?.payload?.messageLog.allData[
                            entry
                        ].map(item => new MessageLog(item));

                return {
                    allData: allData,
                    pageData:
                        rawData?.payload?.messageLog?.pageData?.map(
                            item => new MessageLog(item),
                        ) ?? [],
                    virtualSpec:
                        rawData?.payload?.messageLog?.virtualSpec ??
                        ({
                            page: 0,
                            size: 20,
                            total: 0,
                        } as PageSpecification),
                    internalSpec:
                        rawData?.payload?.messageLog?.internalSpec ??
                        ({
                            page: -1,
                            size: 200,
                            total: 0,
                        } as PageSpecification),
                    status: "idle",
                    openStatus: "idle",
                    error: null,
                };
            })
            .addCase(fetchMessageLogPage.pending, (state, action) => {
                state.status = "loading";
                state.virtualSpec.loading = true;
                state.internalSpec.loading = true;
            })
            .addCase(fetchMessageLogPage.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.virtualSpec.total = action.payload[1];
                state.internalSpec.total = action.payload[1];
                state.virtualSpec.loading = false;
                state.internalSpec.loading = false;
                state.allData[action.payload[0]] = action.payload[2].map(
                    item => new MessageLog(item),
                );

                const internalPageIndex = Math.floor(
                    (state.virtualSpec.page * state.virtualSpec.size) /
                        state.internalSpec.size,
                );
                const internalPage = state.allData[internalPageIndex];
                const translatedPageStart =
                    state.virtualSpec.page * state.virtualSpec.size -
                    internalPageIndex * state.internalSpec.size;
                const translatedPageEnd =
                    translatedPageStart + state.virtualSpec.size;

                state.pageData =
                    internalPage?.slice(
                        Math.min(translatedPageStart, internalPage.length),
                        Math.min(translatedPageEnd, internalPage.length),
                    ) ?? [];

                state.virtualSpec.waiting =
                    state.virtualSpec.loading && state.pageData.length === 0;
            })
            .addCase(fetchMessageLogPage.rejected, (state, action) => {
                state.status = "failed";
                state.virtualSpec.loading = false;
                state.internalSpec.loading = false;
                state.error = action.error.message;
            })
            .addCase(openMessageLog.pending, (state, action) => {
                state.openStatus = "loading";
            })
            .addCase(openMessageLog.fulfilled, (state, action) => {
                state.openMessage = new MessageLog(action.payload[0]);
                state.openRecipients = action.payload[1].map(
                    item => new MessageLogRecipient(item),
                );
                state.openStatus = "succeeded";
            })
            .addCase(openMessageLog.rejected, (state, action) => {
                state.openStatus = "failed";
                state.error = action.error.message;
            });
    },
});

const { setVirtualPageAndSize, clearBlock } = messageLogSlice.actions;

// This is a thunk, they are used to load the state
const fetchMessageLogPage = createAsyncThunk(
    "messageLog/fetchMessageLogPage",
    async (fetchIndex: number) => {
        const { messageLog } = store.getState().db;
        const response = await Promise.all([
            Datastore.getType<number>(`api/MessagingLogs/Internal/Count`),
            Datastore.getType<MessageLog[]>(
                `api/MessagingLogs/Internal/Page/${fetchIndex}/Size/${messageLog.internalSpec.size}`,
            ),
        ]);
        const result = [fetchIndex, response[0], response[1]] as [
            number,
            number,
            MessageLog[],
        ];
        return result;
    },
);
const evaluatePrivateData = createAsyncThunk(
    "emailLog/evaluatePrivateData",
    async (specification: PageSpecification, thunkAPI): Promise<void> => {
        const { messageLog } = store.getState().db;
        const requiredInternalPage = Math.max(
            Math.ceil(
                ((specification.page + 1) * specification.size - 1) /
                    messageLog.internalSpec.size,
            ) - 1,
            0,
        );

        if (messageLog.status !== "loading") {
            if (!messageLog.allData[requiredInternalPage]) {
                await thunkAPI.dispatch(
                    fetchMessageLogPage(requiredInternalPage),
                );
            } else {
                const direction =
                    specification.page > messageLog.virtualSpec.page
                        ? 1
                        : specification.page < messageLog.virtualSpec.page
                        ? -1
                        : 0;

                if (direction !== 0) {
                    const pageGap =
                        direction > 0
                            ? (messageLog.internalSpec.size -
                                  (specification.page * specification.size -
                                      requiredInternalPage *
                                          messageLog.internalSpec.size)) /
                              specification.size
                            : (specification.page * specification.size -
                                  requiredInternalPage *
                                      messageLog.internalSpec.size) /
                              specification.size;

                    if (pageGap < 4) {
                        if (
                            direction < 0 &&
                            requiredInternalPage > 0 &&
                            !messageLog.allData[requiredInternalPage - 1]
                        ) {
                            await thunkAPI.dispatch(
                                fetchMessageLogPage(requiredInternalPage - 1),
                            );
                        } else if (
                            direction > 0 &&
                            !messageLog.allData[requiredInternalPage + 1]
                        ) {
                            await thunkAPI.dispatch(
                                fetchMessageLogPage(requiredInternalPage + 1),
                            );
                        }
                    }
                }
            }
        }
    },
);
export const getMessageLogPage = createAsyncThunk(
    "messageLog/getMessageLogPage",
    async (_, thunkAPI) => {
        const { messageLog } = store.getState().db;
        thunkAPI.dispatch(evaluatePrivateData(messageLog.virtualSpec));
    },
);
export const setMessageLogPageAndSize = createAsyncThunk(
    "messageLog/setMessageLogPageAndSize",
    async (specification: PageSpecification, thunkAPI) => {
        thunkAPI.dispatch(evaluatePrivateData(specification));
        thunkAPI.dispatch(setVirtualPageAndSize(specification));
    },
);
export const deleteMessage = createAsyncThunk(
    "messageLog/deleteMessage",
    async (message: MessageLog, thunkAPI) => {
        const { messageLog } = store.getState().db;
        await Datastore.delete(
            `api/MessagingLogs/Internal/${encodeURIComponent(
                message.id.toString(),
            )}`,
        );
        thunkAPI.dispatch(clearBlock(message));
        thunkAPI.dispatch(evaluatePrivateData(messageLog.virtualSpec));
    },
);
export const openMessageLog = createAsyncThunk(
    "emailLog/openEmailLog",
    async (message: MessageLog, thunkAPI) => {
        const data = await Promise.all([
            Datastore.getType<MessageLog>(
                `api/MessagingLogs/Internal/${message.id}`,
            ),
            Datastore.getType<any[]>(
                `api/MessagingLogs/Internal/${message.id}/Recipients`,
            ),
        ]);

        const matches = [...data[0].body.matchAll(imageRegex)];
        const images = [];

        for (const match of matches)
            if (!images.find(item => item.match[2] === match[2]))
                images.push({
                    match: match,
                    promise: Datastore.getType<MessageLogRecipient>(
                        `api/MessagingLogs/${message.id}/Attachments/${match[2]}`,
                    ),
                });

        const responses = await Promise.all(images.map(item => item.promise));

        images.forEach(
            (item, index) =>
                (message[0].body = message[0].body.replace(
                    new RegExp(item.match[1], "g"),
                    `data:${responses[index].mime};base64,${responses[index].content}`,
                )),
        );

        return [message[0], message[1]] as [MessageLog, MessageLogRecipient[]];
    },
);

// We have to export the reducer as this is added to the state store
export default messageLogSlice.reducer;
